import { makeObservable, observable } from "mobx";
import Validator, { ValidatedValue, ValidatorModel } from "./validator";
import { ErrorMessages } from "./errorMessage";
import Formatter, { CapitalizationType } from "./formatter";

export enum AddressId {
  apartmentNumber = "apartmentNumber",
  buildingNumber = "buildingNumber",
  city = "city",
  county = "county",
  street = "street",
  zipCode = "zipCode"
}

export interface AddressModel {
  apartmentNumber: ValidatedValue<string>;
  buildingNumber: ValidatedValue<string>;
  city: ValidatedValue<string>;
  county: ValidatedValue<string>;
  street: ValidatedValue<string>;
  zipCode: ValidatedValue<string>;

  validate: () => void;
  clearValidation: () => void;
  formattedApartmentNumber: () => void;
}

interface AddressArguments {
  apartmentNumber?: string;
  buildingNumber?: string;
  city?: string;
  county?: string;
  street?: string;
  zipCode?: string;
}

const addressValidators: ValidatorModel = {
  zipCode: [
    { name: "zipCodeIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
    { name: "zipCodeIsValid", fn: Validator.isValidZipCode, errorMessage: ErrorMessages.address.zipCode }
  ],
  buildingNumber: [
    { name: "buildingNumberIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ],
  apartmentNumber: [
    { name: "buildingNumberIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ],
  city: [
    { name: "cityIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ],
  county: [
    { name: "countyIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ],
  street: [
    { name: "streetIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
  ]
};

export class AddressClass implements AddressModel {

  @observable apartmentNumber: ValidatedValue<string> = new ValidatedValue("", addressValidators.apartmentNumber);
  @observable buildingNumber: ValidatedValue<string> = new ValidatedValue("", addressValidators.buildingNumber);
  @observable city: ValidatedValue<string> = new ValidatedValue("", addressValidators.city);
  @observable county: ValidatedValue<string> = new ValidatedValue("", addressValidators.county);
  @observable street: ValidatedValue<string> = new ValidatedValue("", addressValidators.street);
  @observable zipCode: ValidatedValue<string> = new ValidatedValue("", addressValidators.zipCode);

  constructor(args?: AddressArguments) {
    this.apartmentNumber.value = Formatter.capitalizeWords(args?.apartmentNumber?.trim(), CapitalizationType.full) ?? "";
    this.buildingNumber.value = Formatter.capitalizeWords(args?.buildingNumber?.trim(), CapitalizationType.full) ?? "";
    this.city.value = Formatter.capitalizeWords(args?.city?.trim(), CapitalizationType.full) ?? "";
    this.county.value = Formatter.capitalizeWords(args?.county?.trim(), CapitalizationType.full) ?? "";
    this.street.value = Formatter.capitalizeWords(args?.street?.trim(), CapitalizationType.full) ?? "";
    this.zipCode.value = args?.zipCode?.trim() ?? "";
  }

  public static default() {
    return new AddressClass();
  }

  public static example() {
    return new AddressClass({
      buildingNumber: "1",
      city: "Toruń",
      county: "Toruń",
      street: "Dominikańska",
      zipCode: "87-100"
    });
  }

  public static displayName = {
    apartmentNumber: "Numer lokalu",
    buildingNumber: "Numer budynku",
    city: "Miasto",
    county: "Powiat",
    street: "Ulica",
    zipCode: "Kod pocztowy"
  };

  public static getFieldValue(fieldName: keyof AddressModel, newValue: string) {
    let value: string | number = newValue;
    if (fieldName === AddressId.apartmentNumber) {
      value = Number(newValue);
    }
    return value;
  }

  public validate() {
    this.zipCode.validate();
    this.county.validate();
    this.city.validate();
    this.street.validate();
    this.buildingNumber.validate();
    this.apartmentNumber.validate();
  }

  public clearValidation() {
    this.zipCode.clearValidation();
    this.county.clearValidation();
    this.city.clearValidation();
    this.street.clearValidation();
    this.buildingNumber.clearValidation();
    this.apartmentNumber.clearValidation();
  }

  public formattedApartmentNumber() {
    if (!this.apartmentNumber) {
      return undefined;
    }
    return !(this.apartmentNumber.value === "") ? this.apartmentNumber.value : undefined;
  }

  public isEqualTo(address: AddressClass) {
    const actualAddress = JSON.stringify(this) === undefined ? JSON.stringify(this) : JSON.stringify(this).toLowerCase();
    const compareAddress = JSON.stringify(address) === undefined ? JSON.stringify(address) : JSON.stringify(address).toLowerCase();
    return actualAddress === compareAddress;
  }
}

export interface HogaDataModel {
  Id: string;
  Text: string;
}

export interface HogaResponseModel {
  Result: string[];
  IsSuccess: number;
}

export enum HogaResponseDataId {
  firstNames = 1,
  lastNames,
  streets,
  cities,
  counties
}

export enum HogaRequestDataTypeId {
  zipCode = "kodPocztowy",
  city = "miasto",
  county = "powiat",
  streetPrefix = "prefix",
  limit = "limit"
}

export enum SimpleAddressId {
  buildingAndApartmentNumber = "buildingAndApartmentNumber",
  city = "city",
  county = "county",
  street = "street",
  zipCode = "zipCode"
}

export interface SimpleAddressModel {
  buildingAndApartmentNumber: ValidatedValue<string>;
  city: ValidatedValue<string>;
  street: ValidatedValue<string>;
  zipCode: ValidatedValue<string>;
}

interface SimpleAddressArguments {
  buildingAndApartmentNumber?: string;
  city?: string;
  street?: string;
  zipCode?: string;
}

export class SimpleAddressClass implements SimpleAddressModel {
  @observable buildingAndApartmentNumber: ValidatedValue<string> = new ValidatedValue("", addressValidators.buildingNumber);
  @observable city: ValidatedValue<string> = new ValidatedValue("", addressValidators.city);
  @observable street: ValidatedValue<string> = new ValidatedValue("", addressValidators.street);
  @observable zipCode: ValidatedValue<string> = new ValidatedValue("", addressValidators.zipCode);

  constructor(args?: SimpleAddressArguments) {
    makeObservable(this);

    this.buildingAndApartmentNumber.value = Formatter.capitalizeWords(args?.buildingAndApartmentNumber?.trim(), CapitalizationType.full) ?? "";
    this.city.value = Formatter.capitalizeWords(args?.city?.trim(), CapitalizationType.full) ?? "";
    this.street.value = Formatter.capitalizeWords(args?.street?.trim(), CapitalizationType.full) ?? "";
    this.zipCode.value = args?.zipCode?.trim() ?? "";
  }

  public static default() {
    return new SimpleAddressClass();
  }

  public static displayName = {
    buildingAndApartmentNumber: "Nr domu/Nr mieszkania",
    city: "Miasto",
    street: "Ulica",
    zipCode: "Kod pocztowy"
  };

  public clearErrorMessages() {
    this.buildingAndApartmentNumber.setErrorMessage("");
    this.city.setErrorMessage("");
    this.street.setErrorMessage("");
    this.zipCode.setErrorMessage("");
  }
}
